// D1 model_subsegment routing — canonical-17 subsegment label → the market's
// model cell key (each export keys cells in its own vocabulary).
//
// Registry-side CONFIG, not per-market code branches: flipping a market adds
// its map here (checked against the export's actual cell keys by the
// registry tests). An unmapped canonical routes to no cell → serveModelled
// falls to the market-level path or returns null (modelled-pending) — the
// flip dry-run diff reports unrouted labels so nothing is silently dropped.
//
// Fine specialties (D2): enm.primary_specialty routes AHEAD of the canonical
// for the buckets family (DE spec §3, BUCKET_FINE_NORMALISE in serve.ts).
// Which detection grades may route is FAMILY-AWARE (DE flip ruling
// 2026-07-08): the router must never be stricter than the calibration's own
// detection net — fail-closed where the training pool was grade-filtered
// IS train/serve divergence where it wasn't.

import {
  CANONICAL_SUBSEGMENTS,
  SUBSEGMENT_GROUP,
  CANONICAL_SUBSEGMENT_TOKEN,
  type CanonicalSubsegment,
} from '@hc/shared';
import { classifyFrSubsegment } from '../revenue-model/fr-naf-map.js';

/** The D1 floor — grades allowed to route a fine specialty for families
 *  whose calibration filtered by grade. */
export const FINE_ROUTING_GRADES: ReadonlySet<string> = new Set(['native', 'register']);

/** calibration_buckets trained on name-detected fines with no grade filter
 *  (verified in the calibration generator; the cell Ps were MEASURED on
 *  name-graded assignments) — so name routes for this family. */
const BUCKETS_FINE_ROUTING_GRADES: ReadonlySet<string> = new Set(['native', 'register', 'name']);

/** Grades allowed to route a fine specialty, per model family. */
export function fineRoutingGrades(schemaVariant: string): ReadonlySet<string> {
  return schemaVariant === 'calibration_buckets'
    ? BUCKETS_FINE_ROUTING_GRADES
    : FINE_ROUTING_GRADES;
}

/** Wave-1 identity map: the NL/SE/FI/RO exports key their 17 cells by the
 *  canonical labels themselves (loader classification IS canonical-17), so
 *  routing is identity — plus the one platform-side split the models don't
 *  carry: 'Dental laboratories & equipment' folds to its nearest coefficient
 *  ('Medical equipment & supplies'; the DE/GB/DK/BE precedent). */
const CANONICAL_IDENTITY_ROUTES: Readonly<Record<string, string>> = Object.fromEntries(
  CANONICAL_SUBSEGMENTS.map((s) => [
    s,
    s === 'Dental laboratories & equipment' ? 'Medical equipment & supplies' : s,
  ]),
);

/** Per-market canonical-17 → model cell key. Only flipped/flippable markets
 *  are present; add each market's map with its flip. */
export const MODEL_CELL_ROUTER: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // Wave-1 (NL-2026Q2-v1-assets / SE-2026Q2-v1-revenue / FI-2026Q2-v1 /
  // RO-2026Q2-v1): identity — the exports' cell keys ARE the canonical-17.
  // The guard test pins every route against each vendored export's cells.
  NL: CANONICAL_IDENTITY_ROUTES,
  SE: CANONICAL_IDENTITY_ROUTES,
  FI: CANONICAL_IDENTITY_ROUTES,
  RO: CANONICAL_IDENTITY_ROUTES,
  // Wave-2 capacity-anchored (IT/IE/CZ/PL, capacity_rate; ES bespoke — its
  // cells are canonical-keyed too). All classify straight to canonical-17.
  IT: CANONICAL_IDENTITY_ROUTES,
  IE: CANONICAL_IDENTITY_ROUTES,
  PL: CANONICAL_IDENTITY_ROUTES,
  CZ: CANONICAL_IDENTITY_ROUTES,
  ES: CANONICAL_IDENTITY_ROUTES,
  // CH — build_native, entity-only: two group-fit cells ('Hospitals &
  // clinics' keyed by the canonical label itself; 'Residential & home care'
  // reached via the group alias) + 17 floor cells from subsegment_floor_chf.
  // Identity except CH's own label variants; dental-labs folds as usual.
  CH: {
    ...CANONICAL_IDENTITY_ROUTES,
    'Physiotherapy & allied health': 'Physiotherapy & allied',
    'Domiciliary / home care': 'Domiciliary/home care',
  },
  // DE calibration_buckets (CALIB-2026Q3-consolidated-r2-fulldata): snake
  // tokens. Fine cells (imaging, oncology, residential_care) route via
  // primary_specialty, not this map. 'Dental laboratories & equipment' is a
  // platform-side split (non-patient cohort) the DE model doesn't carry —
  // suppliers model as medical_equipment, their nearest coefficient.
  DE: {
    'Hospitals & clinics': 'hospitals_clinics',
    'Primary care (GP)': 'primary_care_gp',
    'Specialist medical': 'specialist_medical',
    Dental: 'dental',
    Optical: 'optical',
    Audiology: 'audiology',
    'Physiotherapy & allied health': 'physio_allied',
    'Diagnostics & labs': 'diagnostics_labs',
    Pharmacy: 'pharmacy',
    'Pharma wholesale': 'pharma_wholesale',
    'Medical equipment & supplies': 'medical_equipment',
    'Dental laboratories & equipment': 'medical_equipment',
    Veterinary: 'veterinary',
    'Residential elderly care': 'residential_elderly',
    'Residential specialist care': 'residential_specialist',
    'Domiciliary / home care': 'domiciliary_home',
    'Ambulance / patient transport': 'ambulance_transport',
    'Other healthcare': 'other_healthcare',
  },
  // GB build_native (05-22 Excel-era labels, refreshed by calibration).
  // INTERIM BORROWS (GB flip ruling 2026-07-08 — not guesses; both cohorts
  // were FIT under these coefficients): Diagnostics & labs → Specialist
  // medical practice (imaging/pathology file SIC 86220; own diagnostics_labs
  // cell queued for the next GB emission — flip the borrow to the own cell
  // when the manifest updates); Other healthcare → Allied health / other
  // (that block IS GB's allied+other cell by construction). UNROUTED:
  // 'Ambulance / patient transport' (no cell, no ruling — pending). The
  // residential split finer than canonical routes via
  // MODEL_CELL_ROUTER_BY_ENUM below.
  GB: {
    'Hospitals & clinics': 'Hospitals',
    'Primary care (GP)': 'GP practices',
    'Specialist medical': 'Specialist medical practice',
    'Diagnostics & labs': 'Specialist medical practice',
    'Other healthcare': 'Allied health / other',
    Dental: 'Dental',
    Optical: 'Opticians',
    Audiology: 'Audiology / hearing aids retail',
    'Physiotherapy & allied health': 'Allied health / other',
    Pharmacy: 'Community pharmacy',
    'Pharma wholesale': 'Pharma wholesale',
    'Medical equipment & supplies': 'Medical / orthopaedic goods retail',
    'Dental laboratories & equipment': 'Medical / orthopaedic goods retail',
    Veterinary: 'Veterinary',
    'Residential elderly care': 'Residential care (elderly)',
    'Residential specialist care': 'Residential care (other)',
    'Domiciliary / home care': 'Domiciliary / social care',
  },
  // DK build_native — exact inversion of DK_SUBSEGMENT_TO_CANONICAL (the
  // model cells ARE the native labels). The one many-to-one fold:
  // 'Residential specialist care' ← LD/MH + other; canonical routes to
  // '(other)' (same blocks, same P 0.833, floors differ — flagged in the
  // flip verdict). INTERIM BORROW (backfill-residual ruling 2026-07-09, GB
  // precedent): Other healthcare → 'Allied health / other' (same role as
  // GB's; own DK cell queued as optional). UNROUTED: Ambulance (no cell
  // despite the native label existing at ingest).
  DK: {
    'Other healthcare': 'Allied health / other',
    'Hospitals & clinics': 'Hospitals',
    'Primary care (GP)': 'GP practices',
    'Specialist medical': 'Specialist medical practice',
    Dental: 'Dental',
    Optical: 'Opticians',
    Audiology: 'Audiology',
    'Physiotherapy & allied health': 'Allied health / other',
    'Diagnostics & labs': 'Diagnostics & labs',
    Pharmacy: 'Community pharmacy',
    'Pharma wholesale': 'Pharma wholesale',
    'Medical equipment & supplies': 'Medical / orthopaedic goods retail',
    'Dental laboratories & equipment': 'Medical / orthopaedic goods retail',
    Veterinary: 'Veterinary',
    'Residential elderly care': 'Residential nursing care',
    'Residential specialist care': 'Residential care (other)',
    'Domiciliary / home care': 'Domiciliary / social care',
  },
  // BE build_native (ln-space, gm/assets/FTE blocks + pooled) — inversion of
  // BE_SUBSEGMENT_TO_CANONICAL. Folds (canonical carries both natives):
  // Specialist medical ← Specialist + Mental health/psychology; Physio ←
  // Physio + Nurses & midwives; Other healthcare ← Alternative + Other human
  // + Childcare → 'Other human health'. UNROUTED: Audiology + Pharma
  // wholesale (no BE cells — the export ships 19 cells without them).
  BE: {
    'Hospitals & clinics': 'Hospitals & clinics',
    'Primary care (GP)': 'GP / general practice',
    'Specialist medical': 'Specialist medical',
    Dental: 'Dental',
    Optical: 'Optical retail',
    'Physiotherapy & allied health': 'Physio / rehab / allied',
    'Diagnostics & labs': 'Medical imaging & labs',
    Pharmacy: 'Pharmacy',
    'Medical equipment & supplies': 'Medical & orthopaedic retail',
    'Dental laboratories & equipment': 'Medical & orthopaedic retail',
    Veterinary: 'Veterinary',
    'Residential elderly care': 'Residential care',
    'Residential specialist care': 'Residential specialist care',
    'Domiciliary / home care': 'Home care & social (no accom.)',
    'Ambulance / patient transport': 'Ambulance / patient transport',
    'Other healthcare': 'Other human health',
  },
  // NO build_native two-resolution — canonical → FINER NO label (floors + P;
  // blocks inherited from the group at adapt time). Audiology + Ambulance
  // have no finer NO cell and fall to their GROUP fit via routeGroupKey.
  NO: {
    'Hospitals & clinics': 'Hospitals & clinics',
    'Primary care (GP)': 'GP / primary care',
    'Specialist medical': 'Specialist medical',
    Dental: 'Dental',
    Optical: 'Optical/medical goods retail (review)',
    'Physiotherapy & allied health': 'Physiotherapy & allied',
    'Diagnostics & labs': 'Diagnostics & labs',
    Pharmacy: 'Pharmacy retail',
    'Pharma wholesale': 'Pharma wholesale',
    'Medical equipment & supplies': 'Medical goods retail',
    'Dental laboratories & equipment': 'Medical goods retail',
    Veterinary: 'Veterinary',
    'Residential elderly care': 'Residential nursing (elderly)',
    'Residential specialist care': 'Residential specialist',
    'Domiciliary / home care': 'Home help / domiciliary',
    'Other healthcare': 'Other healthcare',
  },
  // EE build_native — cells keyed by the FROZEN TOKENS (16 of the 18;
  // audiology + dental_lab_equipment have no EE cells and fall to their
  // GROUP fit via routeGroupKey — the guard test pins the export's set).
  EE: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(
      ([, token]) => token !== 'audiology' && token !== 'dental_lab_equipment',
    ),
  ),
  // Wave-3A (2026-07-10):
  // GR reported_dict — TOKEN-keyed, 11 cells = exactly the 11 subsegments in
  // the GR universe (the other 7 have no GR rows at first serve).
  GR: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(([, token]) =>
      [
        'hospitals_clinics',
        'primary_care_gp',
        'specialist_medical',
        'dental',
        'optical',
        'diagnostics_labs',
        'pharmacy',
        'pharma_wholesale',
        'medical_equipment',
        'veterinary',
        'residential_elderly',
      ].includes(token),
    ),
  ),
  // SI capacity_rate — cells keyed by canonical labels (identity).
  SI: CANONICAL_IDENTITY_ROUTES,
  // LT reported_dict — TOKEN-keyed, 15 cells (no audiology /
  // dental_lab_equipment / domiciliary_home cells in the LT export).
  LT: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(
      ([, token]) => !['audiology', 'dental_lab_equipment', 'domiciliary_home'].includes(token),
    ),
  ),
  // LV v1-calibrated — TOKEN-keyed, all 17 cells (cv_reported ×17).
  LV: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(
      ([, token]) => token !== 'dental_lab_equipment',
    ),
  ),
  // BG v2-loglinear — TOKEN-keyed; canonicals route to the 11 parent cells
  // (the 3 *_other residuals route via the fine tier, not this map). No
  // optical/audiology/wholesale/equipment/dental-lab/vet/ambulance cells.
  BG: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(([, token]) =>
      [
        'dental',
        'diagnostics_labs',
        'domiciliary_home',
        'hospitals_clinics',
        'other_healthcare',
        'pharmacy',
        'physio_allied',
        'primary_care_gp',
        'residential_elderly',
        'residential_specialist',
        'specialist_medical',
      ].includes(token),
    ),
  ),
  // SK v1-full-calibrated — TOKEN-keyed, 11 cells (clinical + social-care
  // scope: no pharmacy/optical/vet/audiology/equipment cells).
  SK: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(([, token]) =>
      [
        'ambulance_transport',
        'dental',
        'diagnostics_labs',
        'domiciliary_home',
        'hospitals_clinics',
        'other_healthcare',
        'physio_allied',
        'primary_care_gp',
        'residential_elderly',
        'residential_specialist',
        'specialist_medical',
      ].includes(token),
    ),
  ),
  // AT capacity_rate — LABEL-keyed, all 17 canonicals carry cells.
  AT: CANONICAL_IDENTITY_ROUTES,
  // PT v2-cells — LABEL-keyed but only 11 canonicals have cells (no Optical /
  // Audiology / Pharma wholesale / Medical equipment / Ambulance / Other).
  PT: Object.fromEntries(
    [
      'Dental',
      'Diagnostics & labs',
      'Domiciliary / home care',
      'Hospitals & clinics',
      'Pharmacy',
      'Physiotherapy & allied health',
      'Primary care (GP)',
      'Residential elderly care',
      'Residential specialist care',
      'Specialist medical',
      'Veterinary',
    ].map((l) => [l, l]),
  ),
  // HU reported_dict — TOKEN-keyed with the v1.1 *_other RESIDUAL keys for
  // three canonicals (the niches live in the specialty tier, the cell prices
  // the parent residual). 17 cells; only dental_lab_equipment has no HU cell.
  HU: {
    ...Object.fromEntries(
      Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(
        ([, token]) => token !== 'dental_lab_equipment',
      ),
    ),
    'Diagnostics & labs': 'diagnostics_other',
    'Residential elderly care': 'residential_elderly_other',
    'Specialist medical': 'specialist_medical_other',
  },
  // HR v2-applied — TOKEN-keyed, 11 cells (5d8a5df904a4). Residential prices
  // on beds_places capacity (§6 vocab extended by insert); the rest are
  // reported-then-median. GP capitation is model-internal and its 'insured'
  // input is NOT staged — GPs fall to the median anchor (flagged).
  HR: Object.fromEntries(
    Object.entries(CANONICAL_SUBSEGMENT_TOKEN).filter(([, token]) =>
      [
        'ambulance_transport',
        'dental',
        'diagnostics_labs',
        'domiciliary_home',
        'hospitals_clinics',
        'other_healthcare',
        'pharmacy',
        'primary_care_gp',
        'residential_elderly',
        'residential_specialist',
        'specialist_medical',
      ].includes(token),
    ),
  ),
};

/** Per-market entities.sub_segment ENUM slug → cell key — platform-side
 *  classification finer than canonical-17 that the market's model actually
 *  has cells for. GB: the SIC enum preserves the residential split
 *  (nursing / medical / elderly / LD-MH / other) that the canonical folds. */
export const MODEL_CELL_ROUTER_BY_ENUM: Readonly<Record<string, Readonly<Record<string, string>>>> =
  {
    GB: {
      residential_nursing: 'Residential nursing care',
      nursing_homes_medical: 'Nursing homes (medical)',
      residential_care_elderly: 'Residential care (elderly)',
      residential_care_mh: 'Residential care (LD/MH)',
      residential_care_other: 'Residential care (other)',
    },
  };

/** Canonicals the enum refinement may act WITHIN, per market. The curated
 *  canonical (panel / reclass) outranks the ingest-time enum — the GB flip
 *  diff caught ~380 rows where a stale residential enum would have dragged
 *  curated Domiciliary/Specialist/GP entities into nursing cells. Refinement
 *  only SPLITS a canonical its market folds, never overrides one. */
export const ENUM_REFINEMENT_CANONICALS: Readonly<Record<string, ReadonlySet<string>>> = {
  GB: new Set(['Residential elderly care', 'Residential specialist care']),
};

/** Markets whose employee predictor was FIT on avg FTE (BE social-balance
 *  code 9087), not year-end headcount — serve must feed the same variable. */
export const AVG_FTE_MARKETS: ReadonlySet<string> = new Set(['BE']);

/** Markets whose entity-level enm.employees is a REGISTER BAND midpoint
 *  (SCB storleksklass), not a filed count (ruling 2026-07-09 (b)): the
 *  recompute routes it to ServeInput.employeesBand (the employee TIER),
 *  never to the regression predictor — cv Ps were measured on filers.
 *
 *  FR joined 2026-08-18. enm.employees for France is INSEE
 *  trancheEffectifsUniteLegale midpoints ('3-5' -> 4, '50-99' -> 75) for
 *  34,733 of 39,152 in-scope entities — a band, by construction, for everyone
 *  who has not filed. FR had been holding ~17k such midpoints in the
 *  filed-count field WITHOUT being declared here, which was harmless only
 *  because model_export_FR.json's predictor vector is balance-sheet only
 *  (log(total_actif), slog(capitaux_propres), slog(disponibilites),
 *  slog(dettes), equity_neg) and never reads employees at all. Declaring it
 *  makes the routing correct by construction instead of correct by luck: the
 *  day FR ships an employees slope, the bands are already fenced off from it.
 *  Measured delta on served FR turnover when this was added: zero. */
export const REGISTER_BAND_EMPLOYEE_MARKETS: ReadonlySet<string> = new Set(['SE', 'FR']);

/** Per-market classifier routing — markets whose model_subsegment derives
 *  from an entity attribute rather than the canonical map. FR: the export's
 *  cells are keyed by the NAF classifier's own labels; classifyFrSubsegment
 *  IS the platform's D1 derivation for FR (non-clinical NAF → null → the
 *  entity never models, exactly as recomputeFrMetrics behaved). */
export const MODEL_CELL_CLASSIFIERS: Readonly<
  Record<string, (naf: string | null) => string | null>
> = {
  FR: classifyFrSubsegment,
};

/** Per-market segment-group cell fallback: canonical → the export's GROUP
 *  cell label (tried when the routed subsegment has no cell — the NO/EE
 *  two-resolution designs put coefficients at group level). Built from the
 *  shared SUBSEGMENT_GROUP contract with each export's own label variants
 *  and folds. */
const EE_GROUP_ALIAS: Readonly<Record<string, string>> = {
  'Hospitals & acute': 'Hospitals & acute',
  'Primary, specialist & allied care': 'Primary/specialist/allied',
  'Pharmacy, diagnostics & supplies': 'Pharmacy/diagnostics/supplies',
  'Residential & home care': 'Residential & home',
  Veterinary: 'Other',
};
const NO_GROUP_ALIAS: Readonly<Record<string, string>> = {
  'Hospitals & acute': 'Hospitals & acute',
  'Primary, specialist & allied care': 'Primary, specialist & allied care',
  'Pharmacy, diagnostics & supplies': 'Pharmacy, diagnostics & supplies',
  'Residential & home care': 'Residential & home care',
  Veterinary: 'Other',
};
const MODEL_GROUP_ALIAS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  EE: EE_GROUP_ALIAS,
  NO: NO_GROUP_ALIAS,
  // CH: one group-fit cell reached via group fallback — residential/home
  // canonicals without their own floor cell fall to the group's fte/beds
  // fits (live once the CH capacity emission merges).
  CH: { 'Residential & home care': 'Residential & home care' },
};

/** Group cell key for a canonical subsegment in a market with group-level
 *  fits — null where the market has no group fallback. */
export function routeGroupKey(cc: string, canonicalSubsegment: string | null): string | null {
  if (!canonicalSubsegment) return null;
  const alias = MODEL_GROUP_ALIAS[cc];
  if (!alias) return null;
  const group = SUBSEGMENT_GROUP[canonicalSubsegment as CanonicalSubsegment];
  if (!group) return null;
  return alias[group] ?? group;
}

/** Route to the market's model cell key: enum refinement first (finer,
 *  platform-derived, scoped to the canonicals it may split), then the
 *  canonical map. Null = unrouted — the serve falls through to market-level /
 *  modelled-pending, never a guessed cell. */
export function routeCellKey(
  cc: string,
  canonicalSubsegment: string | null,
  subSegmentEnum?: string | null,
): string | null {
  if (
    subSegmentEnum &&
    canonicalSubsegment &&
    ENUM_REFINEMENT_CANONICALS[cc]?.has(canonicalSubsegment)
  ) {
    const refined = MODEL_CELL_ROUTER_BY_ENUM[cc]?.[subSegmentEnum];
    if (refined) return refined;
  }
  if (!canonicalSubsegment) return null;
  return MODEL_CELL_ROUTER[cc]?.[canonicalSubsegment] ?? null;
}
